import { Button, Table, Text, Checkbox, Title } from "@deliverr/ui";
import { ShipToStoreCartonLabel, ShipToStoreCartonLabelStatus } from "@deliverr/sortation-client";
import { format } from "date-fns";
import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { PrintJobSummary } from "../useShipToStoreLabel";
import { ShipToStoreModal, shipToStoreModalMap } from "../modals";

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownTrigger = styled.span`
  cursor: pointer;
  font-weight: bold;
  font-size: 17px;
  padding: 8px 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e8e8e8;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 220px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 250px;
  overflow-y: auto;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  cursor: pointer;
  color: black;
  font-weight: normal;
`;

interface ShipToStoreLabelTableProps {
  shipToStoreLabels: ShipToStoreCartonLabel[];
  printLabels: (shipToStoreLabel: ShipToStoreCartonLabel[]) => Promise<void>;
  cancelLabels: (shipToStoreLabel: ShipToStoreCartonLabel[]) => Promise<void>;
  isPrinting: boolean;
  hasConnectedPrinter: boolean;
  printSummary: PrintJobSummary;
  onRefetch: () => Promise<void>;
}

export const ShipToStoreLabelTable: React.FC<ShipToStoreLabelTableProps> = ({
  shipToStoreLabels,
  printLabels,
  cancelLabels,
  isPrinting,
  hasConnectedPrinter,
  printSummary,
  onRefetch,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(
    // these are the preselected statuses when the page loads
    new Set([
      ShipToStoreCartonLabelStatus.GENERATING,
      ShipToStoreCartonLabelStatus.READY,
      ShipToStoreCartonLabelStatus.PRINT_FAILED,
      ShipToStoreCartonLabelStatus.GENERATION_FAILED,
    ])
  );
  const [isStatusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [shouldShowSummary, setShowSummary] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (printSummary.totalLabels > 0) {
      setShowSummary(true);
    }
  }, [printSummary]);

  const toggleStatus = (status: string) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const visibleLabelIds = filteredLabels.map((label) => label.id);
    if (selectedIds.length === visibleLabelIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(visibleLabelIds);
    }
  };

  const handlePrint = async () => {
    const selected = shipToStoreLabels.filter((m) => selectedIds.includes(m.id));
    await printLabels(selected);
  };

  const handleCancel = async () => {
    const selected = shipToStoreLabels.filter((m) => selectedIds.includes(m.id));
    await cancelLabels(selected);
    setSelectedIds([]);
    await onRefetch();
  };

  const handleContinue = async () => {
    setShowSummary(false);
    setSelectedIds([]);
    await onRefetch();
  };

  const statusOptions = [
    { label: "Generating", value: ShipToStoreCartonLabelStatus.GENERATING },
    { label: "Ready", value: ShipToStoreCartonLabelStatus.READY },
    { label: "Printed", value: ShipToStoreCartonLabelStatus.PRINTED },
    { label: "Canceled", value: ShipToStoreCartonLabelStatus.CANCELED },
    { label: "Generation Failed", value: ShipToStoreCartonLabelStatus.GENERATION_FAILED },
    { label: "Print Failed", value: ShipToStoreCartonLabelStatus.PRINT_FAILED },
  ];

  const filteredLabels = shipToStoreLabels
    .filter((label) => selectedStatuses.has(label.status))
    .sort((a, b) => new Date(b.cartonScanTime).getTime() - new Date(a.cartonScanTime).getTime());

  const StatusDropdown = () => (
    <DropdownContainer ref={dropdownRef}>
      <DropdownTrigger
        onClick={(e) => {
          e.stopPropagation();
          setStatusDropdownOpen((prev) => !prev);
        }}
      >
        Status
      </DropdownTrigger>

      {isStatusDropdownOpen && (
        <DropdownMenu onClick={(e) => e.stopPropagation()}>
          {statusOptions.map((option) => (
            <CheckboxLabel key={option.value}>
              <Checkbox checked={selectedStatuses.has(option.value)} onChange={() => toggleStatus(option.value)} />
              {option.label}
            </CheckboxLabel>
          ))}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );

  return (
    <div>
      {isPrinting && shipToStoreModalMap[ShipToStoreModal.LOADING]({ isPrinting })}
      {shouldShowSummary &&
        shipToStoreModalMap[ShipToStoreModal.SUMMARY]({
          shouldShowSummary,
          printSummary,
          onContinue: handleContinue,
        })}

      <HeaderContainer>
        <Title>Ship To Store Labels</Title>
        <ButtonContainer>
          <Button onClick={handlePrint} disabled={selectedIds.length === 0 || isPrinting || !hasConnectedPrinter}>
            Print {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}Labels
          </Button>
          <Button onClick={handleCancel} disabled={selectedIds.length === 0} secondary>
            Cancel
          </Button>
        </ButtonContainer>
      </HeaderContainer>

      <Table
        headings={[
          <Checkbox
            key="select-all"
            checked={selectedIds.length === filteredLabels.length}
            onChange={toggleSelectAll}
          />,
          "Id",
          <StatusDropdown key="status-dropdown" />,
          "Carton Barcode",
          "Tracking Code",
          "Shipping Method",
          "Scanned By",
          "Last Scanned At",
        ]}
        rows={filteredLabels.map((label) => [
          <Checkbox
            key={`select-${label.id}`}
            checked={selectedIds.includes(label.id)}
            onChange={() => toggleSelect(label.id)}
          />,
          <Text key={`id-${label.id}`}>{label.id}</Text>,
          <Text key={`prov-${label.id}`}>{label.status}</Text>,
          <Text key={`mid-${label.id}`}>{label.cartonId}</Text>,
          <Text key={`mid-${label.id}`}>{label.labelTrackingCode}</Text>,
          <Text key={`prov-${label.id}`}>{label.labelShippingMethod}</Text>,
          <Text key={`wh-${label.id}`}>{label.scannedBy}</Text>,
          <Text key={`gen-${label.cartonScanTime}`}>
            {format(new Date(label.cartonScanTime), "MMMM dd, yyyy, h:mmaaa")}
          </Text>,
        ])}
        caption="Ship To Store Labels"
      />
    </div>
  );
};
